/**
 * The blog's shape, as this app understands it.
 *
 * Nothing here names a CMS. Sanity is the source of these types today, behind
 * `sources/sanity`; swapping it is a new folder there and nothing else.
 *
 * This module is also the client-safe half: route components import it, so
 * anything added here reaches the browser bundle. Loading and sanitizing live
 * in `loadBlog.ts`, which does not.
 */
export const BLOG_BASE_PATH = "/blog";

declare const __CANONICAL_ORIGIN__: string | null | undefined;

/**
 * Where every canonical starts, the page's path appended: the CMS's global
 * canonical URL (`siteSettings`, read when the bundle is built, see
 * `buildTimeCms.ts`), else the marketing host, which is where these URLs live.
 */
export const CANONICAL_ORIGIN =
  (typeof __CANONICAL_ORIGIN__ === "string" && __CANONICAL_ORIGIN__) ||
  "https://www.webshare.io";

export type ContentImage = {
  url: string;
  alt: string;
  /**
   * The original's natural size. Only the resized-variant ladder needs it, and
   * only where the asset was measured, so it is nullable rather than assumed.
   */
  width: number | null;
  height: number | null;
  /**
   * Responsive candidates ready for a `srcSet` attribute, empty where the
   * natural width is unknown. The adapter builds this because only the adapter
   * knows how its CMS names resized copies.
   */
  srcset: string;
};

export type Author = {
  name: string;
  slug: string;
  jobTitle: string;
  /** Long-form, for the schema. The published card never prints this one. */
  bio: string;
  /**
   * The CMS keeps a second, shorter bio and it is the one both published cards
   * render. On most authors the two fields hold the same sentence, which is why
   * the difference only shows on the few who wrote a longer `bio`.
   */
  cardBio: string;
  avatar: ContentImage | null;
  socials: AuthorSocials;
};

/**
 * The links the author page prints beside the bio, empty where the author has
 * none. Webflow filed the LinkedIn one under `instagram-link`, and every value
 * in that field is a LinkedIn profile that the published page renders with a
 * LinkedIn icon, so it is named here for what it holds.
 */
export type AuthorSocials = {
  twitter: string | null;
  linkedin: string | null;
  github: string | null;
};

export type Faq = { question: string; answer: ArticleBody };

/**
 * The label colour is picked per category in the CMS from a named set of
 * tints, and resolved to the value the chip is drawn with by the adapter.
 *
 * `href` is the academy's: its categories carry an absolute link to their hub
 * page and the published chip is wrapped in it. The blog's carry none, and the
 * published article still links its chip to `/blog-categories/<slug>`, so that
 * one is built from the slug by `blogCategoryPath`. The listing cards link
 * neither, published or here.
 */
export type Category = {
  name: string;
  slug: string;
  color: string;
  href: string | null;
  /** The blurb the category's own listing page prints as its meta description. */
  description: string;
};

/** An `h2`/`h3`/`h4` in the body, for the in-article contents rail. */
export type Heading = { id: string; text: string; level: 2 | 3 | 4 };

/**
 * An article body, in whichever form its CMS holds it.
 *
 * Webflow stores rich text as an HTML string, so that variant carries markup
 * this app has to sanitize before rendering. A CMS that stores structured text,
 * Sanity's Portable Text and the open spec behind it, carries blocks the app
 * renders with its own components, which is why the two are distinct rather
 * than flattened into one. The `html` variant retires with Webflow.
 */
export type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

export type ArticleBody =
  | { kind: "html"; html: string }
  | { kind: "blocks"; blocks: Json[] };

export type CallToAction = {
  title: string;
  subtitle: string;
  buttonLabel: string;
  href: string;
  /** Only where the published control opens a new tab. */
  newTab?: boolean;
};

/** What the listing needs, deliberately without the body, which is the bulk. */
export type BlogPostSummary = {
  slug: string;
  /** The `<h1>`; differs from `seoTitle` on roughly one post in ten. */
  title: string;
  /** The `<title>` and `og:title`. */
  seoTitle: string;
  summary: string;
  thumbnail: ContentImage | null;
  category: Category | null;
  author: Author | null;
  minutesToRead: string;
  // Three timestamps that read alike and are not interchangeable: `updatedAt`
  // is the date the page shows, `createdAt` fixes the listing order so editing
  // a post cannot move it, and `publishedAt` is the last publish, which the
  // structured data reports as `datePublished`.
  updatedAt: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  /** Editors flag one post to head the listing; position does not decide it. */
  featured: boolean;
};

/** The walkthrough a how-to post opens with, above its body. */
type VideoEmbed = { url: string; title: string };

export type BlogPost = BlogPostSummary & {
  /** The meta description: the summary, unless the SEO field overrides it. */
  seoDescription: string;
  /** The SEO tab's canonical, where the post names another as the original. */
  seoCanonical?: string;
  /** The SEO tab's share title, where the post sets one apart from its title. */
  seoSocialTitle?: string;
  body: ArticleBody;
  video: VideoEmbed | null;
  headings: Heading[];
  image: ContentImage | null;
  /** Webflow's Featured image: printed nowhere on the page, only in its structured data. */
  featuredImage: ContentImage | null;
  faqs: Faq[];
  cta: CallToAction | null;
};

/** One control in a hub's filter panel: a taxonomy item, by its slug. */
export type AcademyFilter = { slug: string; title: string };

/**
 * One chip in the academy article's taxonomy row. `href` is the taxonomy
 * item's own link, which the CMS often leaves empty. The published chip then
 * carries a literal "#", so it is a label that looks like a link and is not.
 */
export type TaxonomyChip = { name: string; href: string };

/**
 * The row's four slots, in the published order: languages, framework,
 * operating system, topic. A slot with nothing in it is still a slot: the
 * published page emits an empty box that still takes one of the row's gaps,
 * so dropping it would slide every later chip 8px left.
 */
export const TAXONOMY_SLOTS = [
  "academy-languages",
  "academy-framework",
  "academy-tools",
  "academy-topic",
] as const;

/**
 * The panel down the left of every hub. Filters, not links: see the note in
 * `loadAcademyHubs.ts` for why the published page's `href="#"` matters.
 *
 * Its contents are taken from the published page rather than derived, because
 * both things about it are authored and neither is in the CMS. WHICH items it
 * offers: only those with a hub page, the Languages group showing five of the
 * collection's eight and Scrape Targets one of sixteen. And in WHAT ORDER:
 * JavaScript, Node.js, Python, C, Bash (Shell), which is neither the
 * collection's order nor alphabetical. Two of the seven groups the published
 * panel holds are drawn at height zero there and are not carried at all, and
 * those two are the ones whose items have no pages. The authored list repeats
 * the group's own label at its head, which an adapter drops.
 *
 * `categories` holds only those the CMS gives a page: the collection has two
 * and the published panel shows one, because `E2E Testing` carries no link and
 * is rendered at height zero.
 */
export type AcademyHubRail = {
  categories: AcademyFilter[];
  groups: Array<{ label: string; segment: string; items: AcademyFilter[] }>;
};

/**
 * A hub's card, carrying what the panel filters on.
 *
 * A summary and not an article: a card shows no body, and running the body
 * pipeline for all seventy-nine on every one of fourteen hubs is about eleven
 * hundred pointless renders, enough to exhaust the dev server's heap.
 */
export type AcademyHubArticle = BlogPostSummary & {
  taxonomies: string[];
  categorySlug: string;
};

/**
 * One of the academy's fourteen hub pages, half CMS and half not.
 *
 * Which articles it lists is entirely CMS: each article multi-references the
 * taxonomy items it belongs to, and filtering on that reproduces the published
 * card counts exactly on 13 of the 14. The fourteenth is the category hub,
 * which lists every published article rather than only its own category's 77;
 * that is the published behaviour and it is reproduced rather than corrected.
 *
 * What is NOT in the CMS is the writing and the filter panel: each hub's
 * heading, its paragraph, its `<title>`, and which taxonomy items the panel
 * offers in what order. Those were recovered once from the published pages
 * into `src/content/academyHubs.json`, which is page copy rather than any
 * CMS's content and so sits above the adapter, which joins it by path.
 */
/** What the code-built home page reads from the CMS: one video, or nothing yet. */
export type HomePageCms = { heroVideo: string | null };

export type AcademyHub = {
  path: string;
  /**
   * `category`, or the URL segment of the taxonomy this hub belongs to, so the
   * panel can open its own group and the breadcrumb can name it. Both of those
   * are what the published page does.
   */
  kind: string;
  slug: string;
  /**
   * The authored trail, not a derived one: it does not always echo the heading,
   * and the Bash hub reads "Bash" here and "Bash (Shell)" above its cards. The
   * category hub's is a placeholder it never renders, so only a full three-part
   * trail is carried and it is left empty.
   */
  crumbs: string[];
  title: string;
  /** The meta description, which on the published hubs is its own text, not the intro. */
  description: string;
  /** Markup, not words: the paragraph bolds the tools it names. */
  descriptionHtml: string;
  documentTitle: string;
  rail: AcademyHubRail;
  articles: AcademyHubArticle[];
};

/**
 * The academy's article: a blog post minus the two fields its collection does
 * not have (no FAQ pairs and no rail call to action) plus the rich-text list of
 * popular articles the published page renders below the body, empty where the
 * article sets none. Modelled off `BlogPost` because every other field is the
 * same field with the same CMS slug, and two independent copies would drift.
 */
export type AcademyArticle = Omit<
  BlogPost,
  "faqs" | "cta" | "featuredImage"
> & {
  popularArticles: ArticleBody;
  /** One entry per slot in `TAXONOMY_SLOTS`, empty slots included. */
  chipSlots: TaxonomyChip[][];
};

export function blogPostPath(slug: string): string {
  return `${BLOG_BASE_PATH}/${slug}`;
}

export function blogPostCanonicalUrl(slug: string): string {
  return `${CANONICAL_ORIGIN}${blogPostPath(slug)}`;
}

/**
 * The academy's article path. Not `/academy/<slug>`: the published site serves
 * articles from `/academy-article/<slug>` and reserves `/academy/...` for
 * fourteen hand-built hub pages; `/academy` itself is a 404 there.
 */
const ACADEMY_ARTICLE_BASE_PATH = "/academy-article";
/** Where the academy breadcrumb's first crumb points; there is no index above it. */
export const ACADEMY_BASE_PATH = "/academy/scraping";

export function academyArticlePath(slug: string): string {
  return `${ACADEMY_ARTICLE_BASE_PATH}/${slug}`;
}

export function academyArticleCanonicalUrl(slug: string): string {
  return `${CANONICAL_ORIGIN}${academyArticlePath(slug)}`;
}

export function authorCanonicalUrl(slug: string): string {
  return `${CANONICAL_ORIGIN}/author/${slug}`;
}

export function blogCategoryPath(slug: string): string {
  return `/blog-categories/${slug}`;
}

export function blogCategoryCanonicalUrl(slug: string): string {
  return `${CANONICAL_ORIGIN}${blogCategoryPath(slug)}`;
}

/** Share cards and structured data are read off-site, so they need absolutes. */
export function siteAssetUrl(path: string): string {
  return `${CANONICAL_ORIGIN}${path}`;
}

/** A button: its label and where it goes. */
export type Cta = {
  label: string;
  href: string;
  /** Only where the published anchor carries `target="_blank"`. */
  newTab?: boolean;
};
